import React from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  MessageSquare,
  Wrench,
  Brain,
  Clock,
  ArrowRight,
  Cpu,
  ShieldCheck,
  FileCode2,
  Languages,
} from "lucide-react";
import { PageLayout } from "../components/PageLayout";

const FEATURES = [
  {
    icon: Wrench,
    title: "Soporte Técnico Especializado",
    description:
      "¿Problemas con Pterodactyl, un plugin de Minecraft o tu entorno VPS? OptiShield IA tiene la solución al instante, entrenada con nuestra base de conocimientos.",
    tag: "Expert mode",
  },
  {
    icon: Brain,
    title: "Conocimiento General",
    description:
      "No solo sabemos de servidores. Pedíle a nuestra IA que redacte código, traduzca textos o te explique conceptos complejos de programación y tecnología.",
    tag: "General purpose",
  },
  {
    icon: Clock,
    title: "Disponibilidad 24/7",
    description:
      "Olvídate de esperar respuestas. OptiShield IA está en línea día y noche para asegurar que tus proyectos nunca se detengan.",
    tag: "Always online",
  },
];

const CAPABILITIES = [
  { icon: Cpu, label: "Diagnóstico de servidores" },
  { icon: FileCode2, label: "Redacción de código" },
  { icon: Languages, label: "Traducción de textos" },
  { icon: ShieldCheck, label: "Revisión de seguridad" },
];

export const IAPage = () => {
  return (
    <PageLayout title="OptiShield IA | Tu Asistente Inteligente" showBackgroundOrbs>
      {/* Hero */}
      <section className="relative overflow-hidden osx-noise" data-testid="ia-hero">
        <div className="absolute inset-0 osx-grid-bg opacity-60" aria-hidden />
        <div className="osx-container relative pt-16 sm:pt-24 pb-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-6"
              style={{
                background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))",
                color: "#ffffff",
                boxShadow: "0 8px 22px rgba(2,132,199,0.35)",
              }}
            >
              <Sparkles size={13} strokeWidth={2.8} />
              OptiShield IA · Beta
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.04] tracking-tight"
              style={{ color: "var(--osx-text)" }}
              data-testid="ia-headline"
            >
              Conoce a <span className="osx-gradient-text">OptiShield IA</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-6 text-base sm:text-lg lg:text-xl max-w-xl leading-relaxed"
              style={{ color: "var(--osx-text-muted)" }}
            >
              Tu asistente inteligente personal. Experto en infraestructura de servidores, resolución de problemas técnicos y capaz de conversar sobre cualquier otro tema que necesites.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <a
                href="https://discord.gg/ExE6pGa4df"
                target="_blank"
                rel="noreferrer noopener"
                data-testid="ia-discord-cta"
                className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm sm:text-base font-semibold text-white"
                style={{
                  background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))",
                  boxShadow: "0 12px 30px rgba(2,132,199,0.35)",
                  transition: "transform 0.2s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <MessageSquare size={16} />
                Hablar con la IA en Discord
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55 }}
              className="mt-8 flex flex-wrap gap-2"
            >
              {CAPABILITIES.map((c) => (
                <div
                  key={c.label}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
                  style={{
                    background: "var(--osx-surface)",
                    color: "var(--osx-text-muted)",
                    border: "1px solid var(--osx-border-strong)",
                  }}
                >
                  <c.icon size={12} />
                  {c.label}
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: chat mockup */}
          <div className="lg:col-span-5 relative hidden md:block">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 0.8, 0.25, 1] }}
              className="relative rounded-3xl overflow-hidden"
              style={{
                background: "var(--osx-surface)",
                border: "1px solid var(--osx-border-strong)",
                boxShadow: "var(--osx-shadow-2)",
              }}
            >
              <div
                className="px-5 py-4 flex items-center justify-between"
                style={{ borderBottom: "1px solid var(--osx-border)", background: "var(--osx-surface-2)" }}
              >
                <div className="flex items-center gap-2.5">
                  <span
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-white"
                    style={{ background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))" }}
                  >
                    <Sparkles size={14} strokeWidth={2.6} />
                  </span>
                  <div>
                    <div className="text-sm font-bold" style={{ color: "var(--osx-text)" }}>OptiShield IA</div>
                    <div className="text-[10px] font-mono" style={{ color: "#10b981" }}>● online</div>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <span className="h-2 w-2 rounded-full" style={{ background: "#ef4444" }} />
                  <span className="h-2 w-2 rounded-full" style={{ background: "#f59e0b" }} />
                  <span className="h-2 w-2 rounded-full" style={{ background: "#10b981" }} />
                </div>
              </div>

              <div className="px-5 py-6 space-y-4">
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.6 }}
                  className="flex justify-end"
                >
                  <div
                    className="max-w-[80%] rounded-2xl rounded-br-sm px-4 py-2.5 text-sm text-white"
                    style={{
                      background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))",
                      boxShadow: "0 6px 18px rgba(2,132,199,0.25)",
                    }}
                  >
                    Mi server no arranca, da un error en la consola de Pterodactyl 😅
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.9 }}
                  className="flex items-start gap-2"
                >
                  <span
                    className="mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white"
                    style={{ background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))" }}
                  >
                    <Sparkles size={12} strokeWidth={2.6} />
                  </span>
                  <div
                    className="max-w-[85%] rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm"
                    style={{
                      background: "var(--osx-surface-2)",
                      color: "var(--osx-text)",
                      border: "1px solid var(--osx-border)",
                    }}
                  >
                    Vamos a resolverlo. Pasame las últimas 20 líneas de la consola — suele ser un plugin incompatible o RAM insuficiente asignada.
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 1.2 }}
                  className="flex items-start gap-2"
                >
                  <span
                    className="mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white"
                    style={{ background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))" }}
                  >
                    <Sparkles size={12} strokeWidth={2.6} />
                  </span>
                  <div
                    className="max-w-[85%] rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm flex items-center gap-2"
                    style={{
                      background: "var(--osx-surface-2)",
                      color: "var(--osx-text-muted)",
                      border: "1px solid var(--osx-border)",
                    }}
                  >
                    <span className="inline-flex gap-1">
                      {[0,1,2].map((i) => (
                        <motion.span
                          key={i}
                          className="inline-block h-1.5 w-1.5 rounded-full"
                          style={{ background: "var(--osx-brand-600)" }}
                          animate={{ y: [0, -3, 0], opacity: [0.4, 1, 0.4] }}
                          transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.15 }}
                        />
                      ))}
                    </span>
                    escribiendo…
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative py-16 sm:py-20">
        <div className="osx-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mb-10"
          >
            <div className="text-xs font-mono uppercase tracking-[0.25em] mb-3" style={{ color: "var(--osx-brand-600)" }}>
              Capacidades
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: "var(--osx-text)" }}>
              Una IA con <span className="osx-gradient-text">superpoderes</span>
            </h2>
          </motion.div>

          <div className="grid gap-5 grid-cols-1 md:grid-cols-3">
            {FEATURES.map((f, idx) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                whileHover={{ y: -4 }}
                className="relative p-7 rounded-2xl osx-gradient-border"
                style={{
                  background: "var(--osx-surface)",
                  border: "1px solid var(--osx-border)",
                  boxShadow: "var(--osx-shadow-1)",
                  transition: "box-shadow 0.3s ease",
                }}
                data-testid={`ia-feature-${idx}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="inline-flex h-12 w-12 items-center justify-center rounded-xl"
                    style={{
                      background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))",
                      color: "#ffffff",
                      boxShadow: "0 10px 24px rgba(2,132,199,0.30)",
                    }}
                  >
                    <f.icon size={20} strokeWidth={2.2} />
                  </div>
                  <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-md"
                    style={{ background: "var(--osx-surface-2)", color: "var(--osx-text-muted)", border: "1px solid var(--osx-border)" }}>
                    {f.tag}
                  </span>
                </div>
                <h3 className="text-lg font-bold mb-2 tracking-tight" style={{ color: "var(--osx-text)" }}>
                  {f.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
                  {f.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20">
        <div className="osx-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-3xl p-8 sm:p-12 text-center"
            style={{
              background: "linear-gradient(135deg, var(--osx-brand-600) 0%, var(--osx-accent-cyan) 100%)",
              boxShadow: "0 30px 80px rgba(2,132,199,0.42)",
            }}
          >
            <div className="absolute inset-0 osx-grid-bg opacity-30" aria-hidden />
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
                Empezá a chatear con la IA
              </h2>
              <p className="mt-4 text-base sm:text-lg text-white/85 max-w-xl mx-auto">
                Uníte a nuestra comunidad en Discord y pedíle lo que necesites. Es gratis.
              </p>
              <a
                href="https://discord.gg/ExE6pGa4df"
                target="_blank"
                rel="noreferrer noopener"
                className="mt-7 inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm sm:text-base font-bold"
                style={{ background: "#ffffff", color: "var(--osx-brand-600)", boxShadow: "0 14px 30px rgba(0,0,0,0.18)", transition: "transform 0.2s ease" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
                data-testid="ia-final-cta"
              >
                <MessageSquare size={16} /> Ir al Discord <ArrowRight size={16} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </PageLayout>
  );
};
